import React, { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CommunityBoundary } from "src/Types/monitoring.types";
import { searchCommunityBoundaries } from "src/utils/apicalls";

interface LocationSearchProps {
  disabled?: boolean;
  onCommunitySelect: (community: CommunityBoundary) => void;
}

const LocationSearch: React.FC<LocationSearchProps> = ({
  disabled = false,
  onCommunitySelect,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [communitySuggestions, setCommunitySuggestions] = useState<
    CommunityBoundary[]
  >([]);
  const [communityError, setCommunityError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCommunitySearch = async () => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) return;

    setLoading(true);
    setCommunityError("");
    try {
      const boundaries = await searchCommunityBoundaries(normalizedQuery);
      setCommunitySuggestions(boundaries);
      if (boundaries.length === 0) {
        setCommunityError(t("dashboard.search.noResults"));
      }
    } catch (error) {
      setCommunitySuggestions([]);
      setCommunityError(t("dashboard.search.unavailable"));
    } finally {
      setLoading(false);
    }
  };

  const handleCommunitySelect = (community: CommunityBoundary) => {
    setQuery(community.name);
    setCommunitySuggestions([]);
    setCommunityError("");
    onCommunitySelect(community);
  };

  return (
    <Paper
      elevation={0}
      sx={{ position: "relative", bgcolor: "transparent", overflow: "visible" }}
    >
      <TextField
        fullWidth
        disabled={disabled}
        label={t("dashboard.search.label")}
        placeholder={t("dashboard.search.placeholder")}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleCommunitySearch();
          }
        }}
        helperText={
          communityError || t("dashboard.search.helper")
        }
        inputProps={{ "aria-label": t("dashboard.search.ariaLabel") }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ) : (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                aria-label={t("dashboard.search.buttonLabel")}
                onClick={handleCommunitySearch}
                disabled={disabled || query.trim().length < 2}
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {communitySuggestions.length > 0 && (
        <Paper
          role="listbox"
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            zIndex: 20,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 18px 45px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
        >
          <List disablePadding>
            {communitySuggestions.map((community) => (
              <ListItemButton
                key={community.providerId || community.name}
                role="option"
                onClick={() => handleCommunitySelect(community)}
                sx={{ py: 1.25 }}
              >
                <ListItemText
                  primary={community.name.split(",")[0]}
                  secondary={community.name}
                  primaryTypographyProps={{ fontWeight: 700 }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Paper>
  );
};

export default LocationSearch;
